"use client"

import type Dexie from "dexie"
import { useLiveQuery } from "dexie-react-hooks"
import { useRef } from "react"
import { offlineDb } from "../db"
import type { ResourceSyncStatus, SyncMeta } from "../types"

const SYNC_META_TABLE = "_syncMeta"

interface SyncMetaRow extends SyncMeta {
  id: string
}

/**
 * Pure status mapping from a `_syncMeta` row → `ResourceSyncStatus`. Exported
 * for testing; production callers use `useSyncStatus`.
 *
 * Once a full fetch has landed (`lastFullFetchAt != null`), the resource is
 * considered ready even if a later delta failed — the UI shouldn't flash a
 * skeleton over stale-but-correct data. Only the *initial* failure shows
 * `"error"`.
 */
export function deriveResourceSyncStatus(
  row: SyncMetaRow | null | undefined
): ResourceSyncStatus {
  if (!row) return "cold"
  if (row.lastFullFetchAt != null) return "ready"
  if (row.status === "error") return "error"
  return "syncing"
}

/**
 * Reactive readiness signal for a single offline resource.
 *
 * Returns one of:
 * - `"cold"`    — the engine hasn't reached this resource yet (no `_syncMeta` row).
 * - `"syncing"` — first-load pull in flight, no usable data yet.
 * - `"ready"`   — full snapshot landed; reactive hooks for this resource will
 *                 return real data (stale-while-revalidate semantics).
 * - `"error"`   — first-load pull failed and we have nothing to show.
 *
 * Use `useSyncStatusMany` for sections that depend on multiple resources.
 */
export function useSyncStatus(
  resourceName: string,
  db: Dexie = offlineDb
): ResourceSyncStatus {
  const row = useLiveQuery(
    () => db.table(SYNC_META_TABLE).get(resourceName) as Promise<SyncMetaRow>,
    [resourceName, db],
    undefined as SyncMetaRow | undefined
  )
  return deriveResourceSyncStatus(row)
}

/**
 * Combined readiness for a section that depends on N resources.
 * Returns the worst-case status — anything not `"ready"` blocks the section.
 *
 * Order of precedence (worst to best): `"cold"` > `"syncing"` > `"error"` > `"ready"`.
 */
export function useSyncStatusMany(
  resourceNames: readonly string[],
  db: Dexie = offlineDb
): ResourceSyncStatus {
  const rows = useLiveQuery(
    () =>
      db.table(SYNC_META_TABLE).bulkGet(resourceNames as string[]) as Promise<
        (SyncMetaRow | undefined)[]
      >,
    [resourceNames.join("|"), db],
    [] as (SyncMetaRow | undefined)[]
  )

  if (resourceNames.length === 0) return "ready"
  // useLiveQuery returns the placeholder until the first resolution; treat that
  // as cold so consumers don't briefly flash "ready" before the real status.
  if (!rows || rows.length !== resourceNames.length) return "cold"

  let worst: ResourceSyncStatus = "ready"
  for (const row of rows) {
    const status = deriveResourceSyncStatus(row)
    if (rank(status) > rank(worst)) worst = status
  }
  return worst
}

function rank(status: ResourceSyncStatus): number {
  switch (status) {
    case "ready":
      return 0
    case "error":
      return 1
    case "syncing":
      return 2
    case "cold":
      return 3
  }
}

/**
 * Reactive list of resource names that are not yet `"ready"` — useful for
 * driving "currently loading: X" indicators that swap labels as the cold-start
 * sync progresses. Order matches the input order so consumers can rotate
 * deterministically (e.g. setInterval round-robin).
 */
export function usePendingSyncResources(
  resourceNames: readonly string[],
  db: Dexie = offlineDb
): string[] {
  const rows = useLiveQuery(
    () =>
      db.table(SYNC_META_TABLE).bulkGet(resourceNames as string[]) as Promise<
        (SyncMetaRow | undefined)[]
      >,
    [resourceNames.join("|"), db],
    [] as (SyncMetaRow | undefined)[]
  )

  // Stabilize the returned array reference: useLiveQuery hands us a fresh
  // rows array on every `_syncMeta` write, so without this the result was a
  // new array on every Dexie update — even when the pending set was
  // unchanged. Consumers (cycling loaders, indicators) re-rendered dozens
  // of times per second during cold start and triggered visible jitter.
  const stableRef = useRef<string[]>([])

  let next: string[]
  if (resourceNames.length === 0) {
    next = []
  } else if (!rows || rows.length !== resourceNames.length) {
    // Pre-resolution: every resource is effectively cold, so all of them are
    // pending. Avoids a flash of "all ready" before Dexie returns.
    next = [...resourceNames]
  } else {
    next = []
    for (let i = 0; i < resourceNames.length; i++) {
      if (deriveResourceSyncStatus(rows[i]) !== "ready") {
        next.push(resourceNames[i] as string)
      }
    }
  }

  const prev = stableRef.current
  if (
    prev.length === next.length &&
    prev.every((name, i) => name === next[i])
  ) {
    return prev
  }
  stableRef.current = next
  return next
}
